import "./Whole.css"

import { useCallback, useEffect, useState } from "react"
import { Navigate, Routes, Route } from "react-router-dom"
import styled from "styled-components"

import ForgotPassword from "../Account/ForgotPassword/ForgotPassword"
import CreateAccount from "../Account/CreateAccount/CreateAccount"
import ChangePassword from "../Account/ChangePassword/ChangePassword"

import Login from "../Account/Login/Login"

import UploadedForm from "../Data/Visualize/UploadedForms/UploadedForm"
import UploadHistorySubpage from "../Data/Visualize/UploadsSubpages/UploadHistorySubpage"
import PatientFiles from "../Data/Visualize/Subpages/PatientFiles"

import DataVisualize from "../Data/Visualize/DataVisualize"
import VisualizePatient from "../Data/Visualize/Subpages/VisualizePatient"
import PatientExports from "../Data/Visualize/Subpages/PatientExports"
import { Visualization } from "../../Managers/VisualizationManager/Visualization"

import UserManagement from "../Admin/Users/UserManagement"
import RolesAndPermissions from "../Admin/Users/RolesAndPermissions"
import Projects from "../Admin/Projects/Projects"
import Sites from "../Admin/Sites/Sites"

import ErrorPage from "../Error/ErrorPage"
import { RequireAuth } from "../Error/RequireAuth"
import Clara from "../Clara/Clara"
import ClaraDocuments from "../Clara/ClaraDocuments"
import ClaraDocumentPage from "../Clara/ClaraDocumentPage"

import { MobergLogoSpinner } from "../../Components/MobergLogoSpinner/MobergLogoSpinner"
import InactivityPage from "../Error/InactivityPage"

import { useAuthProvider } from "../../Providers/AuthProvider"
import { FRONTEND_LINKS } from "../../Constants/FrontendLinks"
import { useUploadIntoQueueProvider } from "../../Providers/UploadIntoQueueProvider"
import UploadQueue from "../Data/Visualize/UploadsSubpages/UploadQueue"
import { useModalProvider } from "../../Providers/ModalProvider"
import UploadWindow from "../Data/Visualize/UploadsSubpages/UploadWindow"
import { UploadProcessProvider } from "../../Providers/UploadProcessProvider"
import Toast from "../../Components/Toast/Toast"
import CuringComaLogin from "../Account/CuringComaLogin/CuringComaLogin"
import { useOnMount } from "../../Hooks/useOnMount"
import { MainLayout } from "./MainLayout"
import RMLogin from "../RemoteMonitoring/RMLogin"
import RMInactive from "../RemoteMonitoring/RMInactive"
import { MobergFontFamily } from "../../Components/MobergFont/MobergFont"
import { useWorkspacesProvider } from "../../Providers/WorkspacesProvider"
import { useEnvironmentVariablesProvider } from "../../Providers/EnvironmentVariablesProvider"
import FeatureFlags from "../Internal/FeatureFlags"

function Whole() {
    const uploadIntoQueueProvider = useUploadIntoQueueProvider();
    const [sidebar, setSidebarState] = useState(true)
    const [navPage, setNavPageState] = useState(true)
    const { createModal } = useModalProvider()
    const { selectedWorkspace } = useWorkspacesProvider()
    const { environmentVariables } = useEnvironmentVariablesProvider()
    const [isMobile, setIsMobile] = useState(false)
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768)


    const setSidebar = useCallback(
        newSidebarState => {
            if (newSidebarState !== sidebar) {
                setSidebarState(newSidebarState)
            }
        },
        [sidebar]
    )

    const setNavPage = useCallback(
        newNavPageState => {
            if (newNavPageState !== navPage) {
                setNavPageState(newNavPageState)
            }
        },
        [navPage]
    )

    function renderUploadModal() {
        createModal(
            <UploadProcessProvider>
                <UploadWindow escClose={false} clickOutsideClose={false} />
            </UploadProcessProvider>
        )
    }

    useEffect(() => {
        if (uploadIntoQueueProvider.openLoading === true) {
            renderUploadModal()
        }
    }, [uploadIntoQueueProvider.openLoading])


    const authProvider = useAuthProvider()

    useOnMount(() => {
        authProvider.refreshSession()
    })

    useEffect(() => {
        const handleBeforeUnload = (event) => {
            sessionStorage.removeItem('currentUploads')
        }

        window.addEventListener('beforeunload', handleBeforeUnload)

        // Clean up the event listener on component unmount
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    }, [])

    function isMobileDevice() {
        const regex = /Mobi|Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
        return regex.test(navigator.userAgent);
    }

    useEffect(() => {
        setIsMobile(isMobileDevice())
        if (isMobileDevice()) {
            setNavPage(false)
        }

        const handleResize = () => setIsSmallScreen(window.innerWidth < 768)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    return authProvider.userIsLoggedIn &&
        (authProvider.permissions === undefined || Object.keys(authProvider.permissions).length === 0) ?
        (
            <LoadingWrapper>
                <LoadingContainer>
                    <MobergLogoSpinner />
                    <LoadingTitle>Loading workspace...</LoadingTitle>
                    <LoadingSubtitle>Please wait while we load your workspace.</LoadingSubtitle>
                </LoadingContainer>
            </LoadingWrapper>
        ) : (
            <>
                <Routes>
                    {/* Routes WITHOUT MainLayout (no sidebar/header) - Mobile and Login routes */}
                    <Route
                        path="/"
                        element={
                            <div>
                                {selectedWorkspace === "CURINGCOMA" ? (
                                    <CuringComaLogin setNavPage={setNavPage} />
                                ) : (isMobile && environmentVariables?.FEATURE_FLAG_REMOTE_MONITORING) ? (
                                    <RMLogin setNavPage={setNavPage} />
                                ) : (isMobile && isSmallScreen) ? (
                                    <MobileUnsupported />
                                ) : (
                                    <Login setNavPage={setNavPage} />
                                )}
                            </div>
                        }
                    />
                    <Route
                        path="*"
                        element={
                            <div>
                                {(isMobile && environmentVariables?.FEATURE_FLAG_REMOTE_MONITORING) ? (
                                    <RMLogin setNavPage={setNavPage} />
                                ) : (isMobile && isSmallScreen) ? (
                                    <MobileUnsupported />
                                ) : (
                                    <ErrorPage setNavPage={setNavPage} />
                                )}
                            </div>
                        }
                    />
                    
                    {/* Other login/auth routes without MainLayout */}
                    <Route path={FRONTEND_LINKS.SUBPAGES.FORGOT_PASSWORD.MAIN} element={<ForgotPassword setSidebar={setSidebar} setNavPage={setNavPage} />} />
                    <Route path={FRONTEND_LINKS.SUBPAGES.INACTIVE.MAIN} element={ (isMobile && environmentVariables?.FEATURE_FLAG_REMOTE_MONITORING) ? <RMInactive setNavPage={setNavPage} /> : <InactivityPage setNavPage={setNavPage} />} />
                    <Route path={FRONTEND_LINKS.SUBPAGES.CREATE_ACCOUNT.MAIN} element={<CreateAccount setNavPage={setNavPage} />} />
                    <Route path={FRONTEND_LINKS.SUBPAGES.CHANGE_PASSWORD.MAIN} element={<ChangePassword setNavPage={setNavPage} />} />

                    {/* Routes WITH MainLayout */}
                    <Route element={<MainLayout navPage={navPage} setNavPage={setNavPage} sidebar={sidebar} setSidebar={setSidebar} />}>
                        {/* All the app routes that need sidebar/header */}
                        {<Route
                            exact
                            path="/"
                            element={
                                <div>
                                    {selectedWorkspace === "CURINGCOMA" ?
                                        <CuringComaLogin
                                            setNavPage={setNavPage}
                                        />
                                        :
                                        <Login
                                            setNavPage={setNavPage}
                                        />
                                    }
                                </div>
                            }
                        />
                        }
                        {true &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.FORGOT_PASSWORD.MAIN}
                                element={
                                    <ForgotPassword
                                        setSidebar={setSidebar}
                                        setNavPage={setNavPage}
                                    />
                                }
                            />
                        }

                        {environmentVariables?.FEATURE_FLAG_CLARA && (
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.CLARA.MAIN}
                                element={
                                    <RequireAuth permissions={['data_review:view']} setNavPage={setNavPage}>
                                        <Clara />
                                    </RequireAuth>
                                }
                            />
                        )}
                        {environmentVariables?.FEATURE_FLAG_CLARA && (
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.CLARA.SUBPAGES.DOCUMENTS.MAIN}
                                element={
                                    <RequireAuth permissions={['data_review:view']} setNavPage={setNavPage}>
                                        <ClaraDocuments />
                                    </RequireAuth>
                                }
                            />
                        )}
                        {environmentVariables?.FEATURE_FLAG_CLARA && (
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.CLARA.SUBPAGES.DOCUMENTS.SUBPAGES.DOCUMENT.MAIN}
                                element={
                                    <RequireAuth permissions={['data_review:view']} setNavPage={setNavPage}>
                                        <ClaraDocumentPage />
                                    </RequireAuth>
                                }
                            />
                        )}

                        {/* Routes WITHOUT MainLayout (no sidebar/header) */}
                        {/* <Route
                            path="/"
                            element={
                                <div>
                                    {selectedWorkspace === "CURINGCOMA" ? (
                                        <CuringComaLogin setNavPage={setNavPage} />
                                    ) : isMobile ? (
                                        <RMLogin setNavPage={setNavPage} />
                                    ) : (
                                        <Login setNavPage={setNavPage} />
                                    )}
                                </div>
                            }
                        /> */}

                        {/* <Route
                            path="*"
                            element={
                                <div>
                                    {isMobile ? (
                                        <RMLogin setNavPage={setNavPage} />
                                    ) : (
                                        <ErrorPage setNavPage={setNavPage} />
                                    )}
                                </div>
                            }
                        /> */}
                        {/* <Route
                            path={FRONTEND_LINKS.SUBPAGES.FORGOT_PASSWORD.MAIN}
                            element={<ForgotPassword setSidebar={setSidebar} setNavPage={setNavPage} />}
                        /> */}
                        {/* <Route
                            path={FRONTEND_LINKS.SUBPAGES.INACTIVE.MAIN}
                            element={ isMobile ? <RMInactive setNavPage={setNavPage} /> : <InactivityPage setNavPage={setNavPage} />}
                        /> */}
                        {/* <Route
                            path={FRONTEND_LINKS.SUBPAGES.CREATE_ACCOUNT.MAIN}
                            element={<CreateAccount setNavPage={setNavPage} />}
                        /> */}
                        {/* <Route
                            path={FRONTEND_LINKS.SUBPAGES.CHANGE_PASSWORD.MAIN}
                            element={<ChangePassword setNavPage={setNavPage} />}
                        /> */}

                        {/* Routes WITH MainLayout */}
                        {true && <Route path={FRONTEND_LINKS.SUBPAGES.DATA.MAIN} element={<Navigate to={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.SUMMARY.MAIN} />} />}

                        {
                            authProvider.permissions?.['patient:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.FORMS.SUBPAGES.PATIENT.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['patient:view']}
                                        setNavPage={setNavPage}>
                                        <UploadedForm
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            >
                            </Route>
                        }
                        {authProvider.permissions?.['patient:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.UPLOAD.SUBPAGES.PATIENT.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['patient:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <UploadHistorySubpage
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                            canUpload={true}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }
                        {authProvider.permissions?.['patient:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.FILES.SUBPAGES.PATIENT.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['patient:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <PatientFiles
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }
                        {authProvider.permissions?.['patient:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.SUBPAGES.PATIENTS.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['patient:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <VisualizePatient
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }
                        {authProvider.permissions?.['export:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.SUBPAGES.PATIENTS.SUBPAGES.EXPORTS.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['export:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <PatientExports
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }

                        {authProvider.permissions?.['data_review:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.VISUALIZE.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['data_review:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <DataVisualize
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                            setNavPage={setNavPage}
                                            navPage={navPage}
                                            canUpload={true}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }

                        {authProvider.permissions?.['user:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.ADMIN.MAIN}
                                element={
                                    <Navigate
                                        to={FRONTEND_LINKS.SUBPAGES.ADMIN.SUBPAGES.USER_MANAGEMENT.MAIN}
                                    />
                                }
                            />
                        }

                        {authProvider.permissions?.['user:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.ADMIN.SUBPAGES.USER_MANAGEMENT.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['user:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <UserManagement
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }

                        {authProvider.permissions?.['role:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.ADMIN.SUBPAGES.USER_MANAGEMENT.SUBPAGES.ROLES_AND_PERMISSIONS.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['role:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <RolesAndPermissions
                                            sidebar={sidebar}
                                            setSidebar={setSidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }

                        {authProvider.permissions?.['project:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.ADMIN.SUBPAGES.STUDIES.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['project:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <Projects
                                            sidebar={sidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }

                        {authProvider.permissions?.['site:view'] &&
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.ADMIN.SUBPAGES.SITES.MAIN}
                                element={
                                    <RequireAuth
                                        permissions={['site:view']}
                                        setNavPage={setNavPage}
                                    >
                                        <Sites
                                            sidebar={sidebar}
                                        />
                                    </RequireAuth>
                                }
                            />
                        }
                        {authProvider.permissions?.['env:manage'] && (
                            <Route
                                path={FRONTEND_LINKS.SUBPAGES.INTERNAL.MAIN}
                                element={
                                    <RequireAuth permissions={['env:manage']} setNavPage={setNavPage}>
                                        <FeatureFlags />
                                    </RequireAuth>
                                }
                            />
                        )}
                    </Route>

                    {/* Routes that do not use the Header and Sidebar go here */}
                    {authProvider.permissions?.['patient:view'] && (
                        <Route
                            path={`${FRONTEND_LINKS.SUBPAGES.DATA.SUBPAGES.REVIEW.MAIN}/:patientID/:patientIdentifier/:siteName/:dataObjectID/:dataObjectName`}
                            element={
                                <RequireAuth permissions={['patient:view']} setNavPage={setNavPage}>
                                    <Visualization />
                                </RequireAuth>
                            }
                        />
                    )}

                </Routes>

                {uploadIntoQueueProvider.openQueue && (
                    <div>
                        <UploadQueue />
                    </div>
                )}

                <Toast />
            </>
        )

}

// Styled components for the loading screen
const LoadingWrapper = styled.div`
    background-color: var(--color-surface-primary);
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LoadingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 24px;
`;

const LoadingTitle = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    font-size: 20px;
    color: var(--color-text-primary);
    margin: 0;
    letter-spacing: 0.3px;
`;

const LoadingSubtitle = styled.p`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 14px;
    color: var(--text-secondary-color);
    margin: 0;
    line-height: 1.4;
`;

const MobileUnsupported = () => (
    <MobileUnsupportedWrapper>
        <MobileUnsupportedContainer>
            <MobileUnsupportedTitle>Mobile Access Not Available</MobileUnsupportedTitle>
            <MobileUnsupportedMessage>
                This interface is not supported on mobile devices. Please use a desktop browser to access the Moberg Platform.
            </MobileUnsupportedMessage>
        </MobileUnsupportedContainer>
    </MobileUnsupportedWrapper>
)

const MobileUnsupportedWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100vw;
    height: 100vh;
    background-color: #ffffff;
`

const MobileUnsupportedContainer = styled.div`
    text-align: center;
    padding: 32px;
    max-width: 400px;
`

const MobileUnsupportedTitle = styled.h2`
    font-family: ${MobergFontFamily.HEADER};
    font-weight: 700;
    font-size: 20px;
    color: #1f2937;
    margin: 0 0 16px 0;
`

const MobileUnsupportedMessage = styled.p`
    font-family: ${MobergFontFamily.REGULAR};
    font-size: 16px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0;
`

export default Whole
